import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PricingCard = ({ price, title, description, link,subslink, disclaimer }) => {
  return (
    <div className="group flex flex-col items-center transform transition-all duration-300 hover:-translate-y-2 px-4 mb-8 lg:mb-0">
      <div className="relative bg-white rounded-[20px] p-6 sm:p-8 w-full max-w-[320px] h-auto min-h-[380px] text-center 
                    shadow-[0_10px_30px_rgba(0,0,0,0.08)] 
                    hover:shadow-[0_20px_40px_rgba(28,82,160,0.15)]
                    transition-all duration-300 mb-5 flex flex-col
                    before:absolute before:inset-0 before:rounded-[20px] 
                    before:bg-gradient-to-b before:from-white before:to-blue-50/30 
                    before:opacity-0 before:transition-opacity before:duration-300
                    group-hover:before:opacity-100 overflow-hidden">
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-50 rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
        
        {/* Content with flexible heights for responsiveness */}
        <div className="relative flex flex-col h-full">
          {/* Price Section */}
          <div className="py-4 flex items-center justify-center">
            <div className="text-[2rem] sm:text-[2.5rem] text-[#1C52A0] font-bold transition-transform duration-300 group-hover:scale-110">
              ₹{price}<span className="text-[1rem] sm:text-[1.2rem] font-semibold">/-</span>
            </div>
          </div>
          
          {/* Title Section */}
          <div className="py-3 flex flex-col items-center justify-center">
            <h3 className="text-[1.25rem] sm:text-[1.5rem] text-[#113262] font-bold relative">
              {title}
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-12 h-0.5 bg-blue-500 scale-0 group-hover:scale-100 transition-transform duration-300"></div>
            </h3>
          </div>
          
          {/* Description Section */}
          <div className="py-3 flex items-center justify-center">
            <p className="text-gray-600 text-sm">
              {description}
            </p>
          </div>
          
          {/* Button and Disclaimer Section */}
          <div className="mt-auto pt-6">
            <a href={subslink} target="_blank" rel="noopener noreferrer" className="w-full block mb-4">
              <button className="w-full bg-gradient-to-r from-[#113262] to-[#1C52A0] text-white py-3 rounded-xl
                           transform transition-all duration-300
                           hover:shadow-lg hover:scale-[1.02]
                           active:scale-[0.98] text-sm font-semibold">
                Subscribe now
              </button>
            </a>
            
            <p className="text-gray-600 text-xs leading-relaxed text-left">
              {disclaimer}
            </p>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col items-center space-y-1">
        <Link 
          to={link.to} 
          className="text-blue-700 hover:text-blue-900 text-sm font-medium
                     transform transition-all duration-300 hover:scale-[1.05]
                     hover:bg-blue-50/20 hover:rounded-full px-2 py-1">
          {link.text}
        </Link>
      </div>
    </div>
  );
};

const PricingSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const swipeContainerRef = useRef(null);
  const cardsRef = useRef(null);
  
  const cards = [
    {
      price: "0",
      title: "Mutual Funds",
      description: "Regular plan",
      link: {
        text: "View Direct Mutual Fund Plans",
        to: "/mutual-funds-pricing"
      },
      subslink:"https://club.maxiomwealth.com/login",
      disclaimer: "In Mutual Fund regular plans, AMCs pay us a commission as per SEBI Regulations"
    },
    {
      price: "0",
      title: "Stocks",
      description: "DIY investment without advisory",
      link: {
        text: "View Stock Investment Plans",
        to: "/stocks-pricing"
      },
      subslink:"https://app.maxiomwealth.com/login",
      disclaimer: "No hidden charges or commissions on stock trading"
    },
    {
      price: "0",
      title: "PMS",
      description: "Management fee with profit share above a threshold",
      link: {
        text: "View PMS Investment Plans",
        to: "/pms-pricing"
      },
      subslink:"https://app.maxiomwealth.com/login",
      disclaimer: "PMS fees are charged as a percentage of AUM and profit share"
    }
  ];

  // Update carousel position when activeIndex changes
  useEffect(() => {
    if (cardsRef.current) {
      cardsRef.current.style.transition = 'transform 0.3s ease';
      cardsRef.current.style.transform = `translateX(-${activeIndex * 100}%)`;
    }
  }, [activeIndex]);

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
    setIsSwiping(true);
    setSwipeOffset(0);
    
    // Remove transition during swiping for better responsiveness
    if (cardsRef.current) {
      cardsRef.current.style.transition = 'none';
    }
  };

  const handleTouchMove = (e) => {
    if (!isSwiping) return;
    
    const currentX = e.touches[0].clientX;
    const diff = currentX - touchStartX;
    
    // Limit swiping at the edges with resistance
    if ((activeIndex === 0 && diff > 0) || 
        (activeIndex === cards.length - 1 && diff < 0)) {
      setSwipeOffset(diff / 3); // Add resistance at the edges
    } else {
      setSwipeOffset(diff);
    }
    
    setTouchEndX(currentX);
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;
    
    setIsSwiping(false);
    
    // Restore transition for smooth return
    if (cardsRef.current) {
      cardsRef.current.style.transition = 'transform 0.3s ease';
    }
    
    // Calculate the minimum swipe distance required (20% of container width)
    const containerWidth = swipeContainerRef.current?.clientWidth || 300;
    const minSwipeDistance = containerWidth * 0.2;
    
    // Determine swipe direction and whether it's enough to change slides
    const diff = touchEndX - touchStartX;
    
    if (diff > minSwipeDistance && activeIndex > 0) {
      // Swiped right - go to previous slide
      setActiveIndex(prevIndex => prevIndex - 1);
    } else if (diff < -minSwipeDistance && activeIndex < cards.length - 1) {
      // Swiped left - go to next slide
      setActiveIndex(prevIndex => prevIndex + 1);
    } else {
      // Not enough swipe distance, reset to current slide
      if (cardsRef.current) {
        cardsRef.current.style.transform = `translateX(-${activeIndex * 100}%)`;
      }
    }
    
    setSwipeOffset(0);
  };

  // Add passive event listener for touchmove
  useEffect(() => {
    const container = swipeContainerRef.current;
    if (!container) return;
    
    const handleTouchMovePassive = (e) => {
      handleTouchMove(e);
      // Only prevent default if we're swiping significantly horizontally
      // This allows vertical scrolling to still work
      if (isSwiping && Math.abs(touchEndX - touchStartX) > 10) {
        e.preventDefault();
      }
    };
    
    container.addEventListener('touchmove', handleTouchMovePassive, { passive: false });
    
    return () => {
      container.removeEventListener('touchmove', handleTouchMovePassive);
    };
  }, [isSwiping, touchStartX, touchEndX]);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-[#F5F5F5] to-white min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4">
      {/* Background Decorations */}
      {/* <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-50 rounded-full blur-3xl opacity-30"></div>
      <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-50 rounded-full blur-3xl opacity-30"></div>
       */}
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4 text-center mb-8 sm:mb-12 font-bold pt-8 lg:pt-12 relative">
        Transparent. Performance linked.
      </h1>
      
      {/* Mobile Toggle Menu - Only visible on small and medium screens */}
      <div className="flex items-center justify-center w-full mb-8 lg:hidden">
        <div className="grid grid-cols-3 gap-2 w-full max-w-md">
          {cards.map((item, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`py-3 px-2 text-sm font-medium rounded-xl transition-all duration-300 ${
                activeIndex === index 
                  ? 'bg-[#1C52A0] text-white shadow-md' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {item.title}
            </button>
          ))}
        </div>
      </div>
      
      {/* Mobile & Tablet View - Swipeable Cards */}
      <div 
        ref={swipeContainerRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className="lg:hidden w-full relative z-10 min-h-[500px] overflow-hidden"
      >
        <div className="w-full h-full">
          <div 
            ref={cardsRef}
            className="flex w-full h-full"
            style={{
              transform: isSwiping 
                ? `translateX(calc(-${activeIndex * 100}% + ${swipeOffset}px))` 
                : `translateX(-${activeIndex * 100}%)`
            }}
          >
            {cards.map((card, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-full flex justify-center"
              >
                <PricingCard {...card} />
              </div>
            ))}
          </div>
        </div>
        
        {/* Swipe Indicators */}
        {isSwiping && swipeOffset > 50 && activeIndex > 0 && (
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-[#1C52A0] text-white p-3 rounded-full opacity-70">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </div>
        )}
        
        {isSwiping && swipeOffset < -50 && activeIndex < cards.length - 1 && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-[#1C52A0] text-white p-3 rounded-full opacity-70">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        )}
      </div>
      
      {/* Desktop View - All Cards Side by Side */}
      <div className="hidden lg:flex lg:flex-row justify-center gap-6 max-w-[1200px] mx-auto relative z-10">
        {cards.map((card, index) => (
          <PricingCard key={index} {...card} />
        ))}
      </div>
      
      {/* Mobile & Tablet Navigation Dots */}
      <div className="flex justify-center mt-6 space-x-4 lg:hidden">
        {cards.map((_, index) => (
          <button
            key={index}
            onClick={() => setActiveIndex(index)}
            className={`w-4 h-4 rounded-full transition-all duration-300 ${
              activeIndex === index 
                ? 'bg-[#1C52A0] w-8' 
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default PricingSection;